import React, { useEffect, useState } from 'react';
import { Text, View } from "../../src/Components/Themed"
import * as Font from 'expo-font';
import type { EventProps, ExternalPriceProps, LeagueProps, MarketProps, TradeProps } from '../../src/types';
import { MarketComponentApi, MarketComponentHelpers } from '../../src/MarketComponents/api';
import { APIOverrides, SocketManager } from 'be-components';
import { Image, useWindowDimensions } from 'react-native';
import moment from 'moment-mini';
import { useColors } from '../../src/constants/useColors';
import { Icons } from '../../src/Components';
import TradeText from './components/TradeText';
import { FINALIZED_STATUSES } from '../../src/Competition/api';


type TickerAppProps = {
    league_id:string,
    color_mode?:'light'|'dark'
}

const MIN_SIZE = 350
const MARGIN = 6

const TickerApp = ({ league_id, color_mode }:TickerAppProps) => {
    const Colors = useColors({ mode: color_mode });
    const [ embed_size, setEmbedSize ] = useState({ height:0, width:0 });
    const [auth_state, setAuthState] = useState<any>({});
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ socket_event, setSocketEvent ] = useState<EventProps | undefined>(undefined);
    const [ socket_trade, setSocketTrade ] = useState<TradeProps[]>([])
    const [ ticker_data, setTickerData ] = useState<{
        loading:boolean,
        markets:MarketProps[],
        league_id:string,
        events:EventProps[],
        leagues:LeagueProps[],
        trades:TradeProps[],
        animate:boolean,
        external_prices:ExternalPriceProps[],
    }>({
        loading:false,
        league_id:league_id,
        events: [],
        markets:[],
        animate: false,
        trades: [],
        leagues: [],
        external_prices: []
    });
    const { trades, leagues, events, loading, animate, external_prices, markets } = ticker_data;
    let sorted_events = events.sort((a,b) => {
        return moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix() || parseInt(a.event_id) - parseInt(b.event_id)
    });
    const primary_markets = markets.filter(m => m.primary_market && m.event_type == 'team');
    const { height } = useWindowDimensions();

    const calcWidth = (width:number, min_size:number, margin:number) => {
        //Check how many we can do min size
        let number_to_render = Math.floor(width / min_size)
        let width_no_margin = width / number_to_render
        return width_no_margin - (margin*2)
    }

    const tick_width = calcWidth(embed_size.width, MIN_SIZE, MARGIN);
    useEffect(() => {
        startUp();
    },[]);

    useEffect(() => {
        if(!socket_event){ return }
        let exists = events.find(e => e.event_id == socket_event.event_id)
        if(!exists){ return }
        setTickerData({
            ...ticker_data,
            events: events.filter(e => e.event_id != socket_event.event_id).concat(socket_event)
        })
        setSocketEvent(undefined)
    },[socket_event])

    useEffect(() => {
        if(socket_trade.length == 0){ return }
        let market = markets.find(m => m.market_id == socket_trade[0]?.market_id)
        if(!market){ return }//

        let nt = socket_trade.find(t => t.side == market.trade_side && t.market_type == 'FOR_MONEY');
        if(!nt){ return }
        let matching_trade = trades.find(t => t.event_type == nt.event_type && t.event_id == nt.event_id && t.market_id == nt.market_id && t.side == nt.side && t.side_id == nt.side_id);
        setTickerData({
            ...ticker_data,
            trades: trades.filter(t => t.trade_id != matching_trade?.trade_id).concat(nt),
            animate: true,
        })
        setSocketTrade([]);
    },[socket_trade])

    const startUp = async() => {
        const auth_result = await APIOverrides.start({
            env: 'PRODUCTION',
            no_cache: false
        });
        setAuthState(auth_result);
        MarketComponentApi.setEnvironment();
        getData(league_id);
    }

    const getData = async(league_id:string) => {
        const lgs = await MarketComponentApi.getLeagues();
        const mks = await MarketComponentApi.getMarkets();
        const evs = await MarketComponentApi.getActiveEvents(league_id);
        const lts = await MarketComponentApi.getLatestTradesByEvents('team', evs.map(e => e.event_id));
        setTickerData({
            ...ticker_data,
            events: evs,
            animate:false,
            markets: mks,
            trades: lts.filter(t => t.market_type == 'FOR_MONEY'),
            leagues: lgs
        });
    }

    const [font_loaded, error] = Font.useFonts({
        BarlowBold: require('../assets/fonts/Barlow-Bold.ttf'),
        BarlowSemiBold: require('../assets/fonts/Barlow-SemiBold.ttf'),
        BarlowRegular: require('../assets/fonts/Barlow-Regular.ttf'),
        BarlowLight: require('../assets/fonts/Barlow-Light.ttf'),
        });
    
    const renderTrades = (data: { item:MarketProps, index:number, event:EventProps, is_live:boolean }) => {
        const ev_trades = trades.filter(t => t.event_type == 'team' && t.event_id == data.event.event_id && t.market_id == data.item.market_id);
        const trade_side_trade = ev_trades.find(t => t.side == data.item.trade_side);
        if(!trade_side_trade){ return <></> }
        return (
            <TradeText
                market={data.item}
                trade={trade_side_trade}
                width={tick_width / 2.5 / 3}
                animate={animate}
            />
        )
    }

    const renderEvents = (data: { item:EventProps, index:number }) => {
        const league = leagues.find(l => l.league_id == data.item.league_id);
        let sup_markets = data.item.supported_markets ?? []
        const supported_markets = primary_markets.filter(pm => sup_markets.map(sm => sm.market_id.toString()).includes(pm.market_id.toString())).sort((a,b) => a.market_id - b.market_id);
        const is_live = moment().isAfter(moment(data.item.scheduled_datetime)) ? true : false
        const is_closed = FINALIZED_STATUSES.includes(data.item.status) ? true : false
        return (
            <View style={{ width:tick_width, margin:5, padding:8, borderBottomWidth:1, borderColor:is_closed?Colors.text.error:is_live?Colors.text.success:Colors.borders.light }}>
                <View key={data.item.event_id.toString()} style={{ flexDirection:'row', alignItems:'center' }}>
                    <Image
                        source={{ uri: league?.league_image }}
                        style={{ height:30, width:30 }}
                        resizeMode='cover'
                    />
                    <View style={{ flex:1, marginLeft:10, marginRight:10 }}>
                        <Text theme='h2'>{data.item.event_title}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>{data.item.time_detail == 'scheduled'?`${moment(data.item.scheduled_datetime).format('MMM DD hh:mm a')}`:data.item.time_detail}</Text>
                    </View>
                    <View style={{ flexDirection:'row' }}>
                        {supported_markets.map((sm, i) => {
                            return renderTrades({ item: sm, index:i, event:data.item, is_live })
                        })}
                    </View>
                </View>
                {data.item.last_play ?
                <Text theme='description' style={{ marginTop:3 }}>{data.item.last_play}</Text>
                :<></>}
            </View>
            
        )
    }

    return (
        <View style={{ flex:1, flexDirection:'row' }} >
            <View style={{ flex:1 }} onLayout={(ev) => {
                const { width, height } = ev.nativeEvent.layout;
                setEmbedSize({ width, height });
            }}>
                <View style={{ flexDirection:'row', flexWrap:'wrap' }}>
                    {sorted_events.map((e,i) => {
                        return renderEvents({ item:e, index:i })
                    })}
                </View>
            </View>
            {auth_state.distinct_id ?
            <SocketManager
                distinct_id={auth_state.distinct_id}
                subscribed_events={['V1_UPDATE_LATEST_TRADE','V1_UPDATE_EVENT']}
                onConnect={() => {
                    if(needs_reload){ getData(league_id) }
                }}
                onDisconnect={() => setNeedsReload(true)}
                onSocketEvent={(ev) => {
                    switch(ev.type){
                        case 'V1_UPDATE_LATEST_TRADE':
                            return setSocketTrade(ev.data)
                        case 'V1_UPDATE_EVENT':
                            return setSocketEvent(ev.data)
                        default:return
                    }
                }}
            />
            :<></>}
        </View>
    )
}

export default TickerApp